import React, { useState } from 'react'
import { Mail, Phone, MapPin, Send, CheckCircle, AlertCircle } from 'lucide-react'
import './ContactUs.css'

const STORAGE_KEY = 'mss_contact_submissions'

function saveToStorage(data) {
  try {
    const existing = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]')
    existing.push({ ...data, submittedAt: new Date().toISOString() })
    localStorage.setItem(STORAGE_KEY, JSON.stringify(existing))
    return true
  } catch {
    return false
  }
}

const initialForm = { email: '', phone: '', subject: '', body: '' }

export default function ContactUs() {
  const [form, setForm] = useState(initialForm)
  const [errors, setErrors] = useState({})
  const [submitted, setSubmitted] = useState(false)
  const [saving, setSaving] = useState(false)

  const validate = () => {
    const e = {}
    if (!form.email.trim()) e.email = 'Email address is required.'
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'Please enter a valid email address.'
    if (!form.subject.trim()) e.subject = 'Subject / Header is required.'
    if (!form.body.trim()) e.body = 'Message body is required.'
    return e
  }

  const handleChange = (field, val) => {
    setForm(f => ({ ...f, [field]: val }))
    if (errors[field]) setErrors(e => { const n = { ...e }; delete n[field]; return n })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    const errs = validate()
    if (Object.keys(errs).length) { setErrors(errs); return }
    setSaving(true)
    // Save to localStorage (client-side persistence — replace with API call in production)
    saveToStorage(form)
    // Simulate network delay
    await new Promise(r => setTimeout(r, 800))
    setSaving(false)
    setSubmitted(true)
  }

  const handleReset = () => { setForm(initialForm); setErrors({}); setSubmitted(false) }

  return (
    <div className="contact-page">
      <section className="contact-hero">
        <div className="container contact-hero__inner">
          <span className="section-tag">Get In Touch</span>
          <h1 className="section-title">Contact Us</h1>
          <p className="section-desc" style={{ margin: '0 auto' }}>
            Whether you want to volunteer, donate, partner with us, or simply learn more — we'd love to hear from you.
          </p>
        </div>
      </section>

      <section className="page-section">
        <div className="container">
          <div className="contact-grid">
            {/* Info panel */}
            <div className="contact-info">
              <h2 className="contact-info__title">Reach Us</h2>
              <div className="contact-info__items">
                <div className="contact-info__item">
                  <div className="contact-info__icon"><MapPin size={20}/></div>
                  <div>
                    <div className="contact-info__label">Address</div>
                    <div className="contact-info__val">Officers Line, Balimela, Malkangiri-764051, Odisha, India</div>
                  </div>
                </div>
                <div className="contact-info__item">
                  <div className="contact-info__icon"><Mail size={20}/></div>
                  <div>
                    <div className="contact-info__label">Email</div>
                    <a href="mailto:malyabantaswechhasevisamitibml@gmail.com" className="contact-info__val contact-info__link">
                      malyabantaswechhasevisamitibml@gmail.com
                    </a>
                  </div>
                </div>
                <div className="contact-info__item">
                  <div className="contact-info__icon"><Phone size={20}/></div>
                  <div>
                    <div className="contact-info__label">Phone</div>
                    <a href="tel:+91 84569 59295" className="contact-info__val contact-info__link">+91 84569 59295</a>
                  </div>
                </div>
              </div>
              <div className="contact-hours">
                <h3 className="contact-hours__title">Office Hours</h3>
                <p>Monday – Saturday: 9:00 AM – 5:00 PM IST</p>
                <p>Sunday: Closed</p>
              </div>
              <div className="contact-note">
                <CheckCircle size={16}/>
                <span>We typically respond within 24–48 business hours.</span>
              </div>
            </div>

            {/* Form */}
            <div className="contact-form-wrap">
              {submitted ? (
                <div className="contact-success card">
                  <div className="contact-success__icon"><CheckCircle size={40} color="#0ea5e9"/></div>
                  <h2>Message Received!</h2>
                  <p>
                    Thank you for reaching out. We've saved your message and will reply to <strong>{form.email}</strong> within 24–48 hours.
                  </p>
                  <p className="contact-success__sub">If your query is urgent, please call us directly at <strong>+91 98765 43210</strong>.</p>
                  <button className="btn btn-primary" onClick={handleReset}>Send Another Message</button>
                </div>
              ) : (
                <form className="contact-form card" onSubmit={handleSubmit} noValidate>
                  <h2 className="contact-form__title">Send a Message</h2>

                  <div className="form-field">
                    <label className="form-label" htmlFor="email">Email Address <span className="req">*</span></label>
                    <input
                      id="email"
                      type="email"
                      className={`form-input${errors.email ? ' form-input--error' : ''}`}
                      placeholder="your@email.com"
                      value={form.email}
                      onChange={e => handleChange('email', e.target.value)}
                      autoComplete="email"
                    />
                    {errors.email && <span className="form-error"><AlertCircle size={13}/> {errors.email}</span>}
                  </div>

                  <div className="form-field">
                    <label className="form-label" htmlFor="phone">Phone Number <span className="optional">(optional)</span></label>
                    <input
                      id="phone"
                      type="tel"
                      className="form-input"
                      placeholder="+91 9XXXXXXXXX"
                      value={form.phone}
                      onChange={e => handleChange('phone', e.target.value)}
                      autoComplete="tel"
                    />
                  </div>

                  <div className="form-field">
                    <label className="form-label" htmlFor="subject">Subject / Header <span className="req">*</span></label>
                    <input
                      id="subject"
                      type="text"
                      className={`form-input${errors.subject ? ' form-input--error' : ''}`}
                      placeholder="e.g. Volunteering Inquiry"
                      value={form.subject}
                      onChange={e => handleChange('subject', e.target.value)}
                    />
                    {errors.subject && <span className="form-error"><AlertCircle size={13}/> {errors.subject}</span>}
                  </div>

                  <div className="form-field">
                    <label className="form-label" htmlFor="body">Message <span className="req">*</span></label>
                    <textarea
                      id="body"
                      className={`form-input form-textarea${errors.body ? ' form-input--error' : ''}`}
                      placeholder="Write your message here..."
                      value={form.body}
                      onChange={e => handleChange('body', e.target.value)}
                      rows={6}
                    />
                    {errors.body && <span className="form-error"><AlertCircle size={13}/> {errors.body}</span>}
                  </div>

                  <button type="submit" className="btn btn-primary contact-submit" disabled={saving}>
                    {saving ? (
                      <><span className="contact-spinner"/> Sending…</>
                    ) : (
                      <><Send size={17}/> Send Message</>
                    )}
                  </button>
                  <p style={{fontSize:'0.78rem', color:'var(--gray-400)', textAlign:'center', marginTop:'8px'}}>Fields marked <span className="req">*</span> are mandatory.</p>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
